import { useState, useRef, useEffect } from "react";
import { Send, Sparkles, Loader2, ScrollText } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

/**
 * AIChatPanel — reusable AI-backed chat panel for delegate prep + chair assistant.
 * Hides AI-vendor branding; the assistant identifies itself as Diplomexa.
 */
export default function AIChatPanel({
  title,
  subtitle,
  endpoint,                       // e.g. "/ai/delegate-prep" or "/ai/chair-assist"
  fixedPayload,                   // e.g. { country, committee, agenda }
  starters = [],                  // suggested starter prompts
  api,                            // axios instance
  onAnswer,                       // optional callback (q, a) => void
  modeChips,                      // optional modes [{label, value}]
  initialMode,
  testidPrefix = "ai-chat",
}) {
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(initialMode || (modeChips?.[0]?.value));
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [history]);

  const send = async (q) => {
    const text = (q ?? input).trim();
    if (!text || loading) return;
    setHistory((h) => [...h, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const payload = {
        ...fixedPayload,
        question: text,
        history,
        save_session_id: sessionId || undefined,
      };
      if (modeChips) payload.mode = mode;
      const { data } = await api.post(endpoint, payload);
      setSessionId(data.session_id || sessionId);
      setHistory((h) => [...h, { role: "assistant", content: data.answer, mode }]);
      if (onAnswer) onAnswer(text, data.answer, mode);
    } catch (err) {
      setHistory((h) => [...h, { role: "assistant", content: "Sorry, I couldn't reach the assistant. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200 flex flex-col overflow-hidden h-[640px]" data-testid={testidPrefix}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-[#0A192F] to-[#0F4C81] text-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-[#D4AF37] text-[#0A192F] flex items-center justify-center">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
              Diplomexa AI
            </p>
            <p className="text-base font-semibold tracking-tight">{title}</p>
          </div>
        </div>
        {subtitle && <p className="text-xs text-slate-300 hidden sm:block">{subtitle}</p>}
      </div>

      {/* Mode chips */}
      {modeChips && (
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex flex-wrap gap-2" data-testid={`${testidPrefix}-modes`}>
          {modeChips.map((m) => (
            <button
              key={m.value}
              onClick={() => setMode(m.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.16em] transition-colors ${
                mode === m.value
                  ? "bg-[#0A192F] text-white"
                  : "bg-white border border-slate-200 text-slate-700 hover:border-[#0F4C81]"
              }`}
              data-testid={`${testidPrefix}-mode-${m.value}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4" data-testid={`${testidPrefix}-messages`}>
        {history.length === 0 && (
          <div className="text-center pt-6">
            <ScrollText className="h-8 w-8 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">Ask anything to get started.</p>
            {starters.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2 justify-center">
                {starters.map((s, idx) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-xs text-slate-700"
                    data-testid={`${testidPrefix}-starter-${idx}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {history.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`${testidPrefix}-msg-${m.role}`}
          >
            <div
              className={`max-w-[85%] rounded-xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[#0F4C81] text-white"
                  : "bg-slate-100 text-slate-800 border border-slate-200"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start" data-testid={`${testidPrefix}-loading`}>
            <div className="bg-slate-100 rounded-xl px-4 py-3 inline-flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="px-4 py-3 border-t border-slate-100 bg-white flex items-end gap-2"
      >
        <Textarea
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Type your question…"
          disabled={loading}
          className="min-h-[40px] resize-none"
          data-testid={`${testidPrefix}-input`}
        />
        <Button
          type="submit"
          disabled={loading || !input.trim()}
          className="bg-[#0F4C81] hover:bg-[#0A3860] h-10 px-3"
          data-testid={`${testidPrefix}-send`}
        >
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}
